import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  BookOpen,
  Eye,
  Heart,
  Music2,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import { api, post } from '../lib/api';
import { ChordViewer } from '../components/ChordViewer';
import { Seo } from '../components/Seo';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
type Song = {
  id: string;
  slug: string;
  title: string;
  artist: string;
  originalKey: string;
  capo: number;
  tuning: string;
  difficulty: string;
  viewCount: number;
  bookmarkCount: number;
  contributor: string;
  publishedAt: string;
};
export function Home() {
  const { data } = useQuery({
    queryKey: ['songs', 'home'],
    queryFn: () => api<{ items: Song[] }>('/chords?limit=6&sort=popular'),
  });
  return (
    <>
      <Seo
        title="ChordVerse | Nepali Guitar Chords & Lyrics"
        description="Discover accurate, community-reviewed Nepali and Hindi guitar chords, lyrics, capo settings, strumming patterns, and instant transposition."
      />
      <section className="hero">
        <div className="eyebrow">
          <Sparkles /> Community-reviewed chords
        </div>
        <h1>
          Every song starts
          <br />
          with a <em>single chord.</em>
        </h1>
        <p>
          Discover accurate guitar chords, transpose instantly, and share the songs you love with
          players everywhere.
        </p>
        <form className="hero-search" action="/browse">
          <Search />
          <input
            name="q"
            aria-label="Search songs"
            placeholder="Search songs, artists, or contributors…"
          />
          <button>Find chords</button>
        </form>
        <div className="trust-row">
          <span>
            <ShieldCheck /> Reviewed by musicians
          </span>
          <span>
            <Music2 /> Instant transposition
          </span>
          <span>
            <Users /> Built by the community
          </span>
        </div>
      </section>
      <section className="page">
        <div className="section-heading">
          <div>
            <span className="kicker">Trending now</span>
            <h2>Chords players love</h2>
          </div>
          <Link to="/browse">
            Browse all <ArrowRight />
          </Link>
        </div>
        <div className="song-grid">
          {data?.items.map((song) => <SongCard key={song.id} song={song} />) ??
            [1, 2, 3].map((i) => <div className="skeleton card" key={i} />)}
        </div>
      </section>
      <section className="dark-band">
        <div>
          <span className="kicker">Share your sound</span>
          <h2>
            Know the chords?
            <br />
            Pass them on.
          </h2>
          <p>
            Help another player learn their favorite song. Every approved contribution brings you
            closer to becoming a community reviewer.
          </p>
          <Link className="button" to="/dashboard/new">
            Submit a chord sheet <ArrowRight />
          </Link>
        </div>
        <div className="stat-orbit">
          <strong>100</strong>
          <span>approved contributions</span>
          <small>unlock reviewer status</small>
        </div>
      </section>
    </>
  );
}
function SongCard({ song }: { song: Song }) {
  return (
    <Link className="song-card" to={`/chords/${song.slug}`}>
      <div className="song-art">
        <Music2 />
        <span>{song.difficulty?.toLowerCase()}</span>
      </div>
      <div className="song-card-body">
        <h3>{song.title}</h3>
        <p>{song.artist}</p>
        <div className="tags">
          <span>Key {song.originalKey}</span>
          {song.capo > 0 && <span>Capo {song.capo}</span>}
        </div>
        <div className="card-meta">
          <span>
            <Eye /> {(song.viewCount ?? 0).toLocaleString()}
          </span>
          <span>
            <Heart /> {song.bookmarkCount ?? 0}
          </span>
        </div>
      </div>
    </Link>
  );
}
export function Browse() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [term, setTerm] = useState(q);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (term !== q) setParams(term ? { q: term } : {});
    }, 350);
    return () => clearTimeout(timer);
  }, [term, q, setParams]);
  const { data, isLoading } = useQuery({
    queryKey: ['songs', q],
    queryFn: () => api<{ items: Song[]; total: number }>(`/chords?q=${encodeURIComponent(q)}`),
  });
  return (
    <div className="page">
      <Seo
        title="Browse Guitar Chords & Lyrics | ChordVerse"
        description="Browse and search community-reviewed Nepali and Hindi guitar chords, lyrics, keys, capo settings, and strumming patterns on ChordVerse."
        path="/browse"
      />
      <div className="page-title">
        <span className="kicker">The songbook</span>
        <h1>Find your next song</h1>
        <p>Search accurate, community-reviewed chord sheets.</p>
      </div>
      <div className="search-bar">
        <Search />
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Song, artist, category, key, tuning…"
          aria-label="Search chord sheets"
        />
      </div>
      <div className="result-line">
        <strong>{data?.total ?? 0}</strong> chord sheets {q && <>for “{q}”</>}
      </div>
      <div className="song-grid">
        {isLoading ? (
          [1, 2, 3, 4, 5, 6].map((i) => <div className="skeleton card" key={i} />)
        ) : data?.items.length ? (
          data.items.map((s) => <SongCard song={s} key={s.id} />)
        ) : (
          <Empty
            title="No chords found"
            text="Try a broader search, or request this song from the community."
          />
        )}
      </div>
    </div>
  );
}
export function ChordDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['chord', slug],
    queryFn: () => api<Record<string, unknown>>(`/chords/${slug}`),
  });
  useEffect(() => {
    if (slug) void post(`/chords/${slug}/view`);
  }, [slug]);
  if (isLoading)
    return (
      <div className="page">
        <div className="skeleton hero-skeleton" />
      </div>
    );
  if (!data) return null;
  const snapshot = data.snapshot as Record<string, unknown>;
  const canManage = user?.role === 'ADMIN' || user?.id === String(data.contributor_id);
  return (
    <div className="song-page">
      <Seo
        title={`${String(data.title)} Chords by ${String(data.artist)} | ChordVerse`}
        description={`Play ${String(data.title)} by ${String(data.artist)} with accurate guitar chords, lyrics, key ${String(data.original_key)}, capo ${String(data.capo)}, and transpose controls.`}
        path={`/chords/${String(slug)}`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'MusicComposition',
          name: String(data.title),
          composer: { '@type': 'MusicGroup', name: String(data.artist) },
          url: `https://www.chordsverse.qd.je/chords/${String(slug)}`,
        }}
      />
      <div className="song-hero">
        <div className="breadcrumbs">
          <Link to="/browse">Chords</Link> /{' '}
          <Link to={`/artists/${data.artistSlug}`}>{String(data.artist)}</Link>
        </div>
        <section
          className={`song-information${canManage ? ' has-actions' : ''}`}
          aria-label="Song information"
        >
          <span className="song-name-fact">
            <strong>{String(data.title)}</strong>
            <span aria-hidden="true">–</span>
            <Link to={`/artists/${data.artistSlug}`}>{String(data.artist)}</Link>
          </span>
          <span>
            <b>Key</b>
            {String(data.original_key)}
          </span>
          <span>
            <b>Capo</b>
            {String(data.capo)}
          </span>
          <span>
            <b>Tuning</b>
            {String(data.tuning)}
          </span>
          <span>
            <b>Difficulty</b>
            {String(data.difficulty).toLowerCase()}
          </span>
          <span>
            <b>Strumming</b>
            {String(snapshot.strummingPattern ?? 'Not specified')}
          </span>
          <span>
            <b>Contributed by</b>
            <Link to={`/contributors/${data.contributorSlug}`}>{String(data.contributor)}</Link>
          </span>
          <span>
            <b>Views</b>
            {Number(data.view_count).toLocaleString()}
          </span>
          {canManage && (
            <div className="song-actions">
              <Link
                className="button secondary small"
                to={`/dashboard/submissions/${String(data.id)}/edit`}
              >
                Edit
              </Link>
              <button
                className="button danger small"
                onClick={async () => {
                  if (!window.confirm('Remove this published song? It will no longer be public.'))
                    return;
                  try {
                    await post(`/chords/${String(data.id)}/archive`);
                    toast.success('Published song removed');
                    void client.invalidateQueries({ queryKey: ['songs'] });
                    navigate('/browse');
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : 'Unable to remove song.');
                  }
                }}
              >
                Remove
              </button>
            </div>
          )}
        </section>
      </div>
      <ChordViewer content={String(snapshot.content ?? '')} initialCapo={Number(data.capo)} />
      <aside className="song-notes">
        <h2>Playing notes</h2>
        {Boolean(snapshot.notes) && <p>{String(snapshot.notes)}</p>}
        {!snapshot.notes && <p>No additional playing notes.</p>}
      </aside>
    </div>
  );
}
type DirectoryItem = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  biography?: string;
};
export function Directory({ kind }: { kind: 'artists' | 'categories' }) {
  const { data } = useQuery({
    queryKey: [kind],
    queryFn: () => api<{ items: DirectoryItem[] }>(`/${kind}`),
  });
  return (
    <div className="page">
      <Seo
        title={`${kind === 'artists' ? 'Artists' : 'Song Categories'} | ChordVerse`}
        description={`Explore ${kind === 'artists' ? 'artists' : 'Nepali and Hindi song categories'} and find accurate guitar chords and lyrics on ChordVerse.`}
        path={`/${kind}`}
      />
      <div className="page-title">
        <span className="kicker">Explore</span>
        <h1>{kind === 'artists' ? 'Artists' : 'Categories'}</h1>
        <p>
          {kind === 'artists'
            ? 'Find songs from the voices you love.'
            : 'Choose a sound and start playing.'}
        </p>
      </div>
      <div className="directory-grid">
        {data?.items.map((item) => (
          <Link className="directory-card" to={`/${kind}/${item.slug}`} key={item.id}>
            <div className="avatar">{item.name.slice(0, 2).toUpperCase()}</div>
            <h3>{item.name}</h3>
            <p>{item.description ?? item.biography ?? `Explore ${item.name} chord sheets.`}</p>
            <span>
              View songs <ArrowRight />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
export function DirectoryDetail({ kind }: { kind: 'artists' | 'categories' }) {
  const { slug } = useParams();
  const { data } = useQuery({
    queryKey: [kind, slug],
    queryFn: () => api<{ item: DirectoryItem; songs: Song[] }>(`/${kind}/${slug}`),
  });
  if (!data)
    return (
      <div className="page">
        <div className="skeleton hero-skeleton" />
      </div>
    );
  return (
    <div className="page">
      <Seo
        title={`${data.item.name} Guitar Chords & Lyrics | ChordVerse`}
        description={`Browse ${data.item.name} songs with accurate guitar chords, lyrics, capo settings, and transpose controls on ChordVerse.`}
        path={`/${kind}/${String(slug)}`}
      />
      <div className="page-title">
        <span className="kicker">{kind === 'artists' ? 'Artist' : 'Category'}</span>
        <h1>{data.item.name}</h1>
        <p>{data.item.description ?? data.item.biography}</p>
      </div>
      <div className="song-grid">
        {data.songs.map((s) => (
          <SongCard key={s.id} song={s} />
        ))}
      </div>
    </div>
  );
}
export function Contributor() {
  const { slug } = useParams();
  const { data } = useQuery({
    queryKey: ['contributor', slug],
    queryFn: () =>
      api<{
        user: { displayName: string; biography?: string; approvedContributionCount: number };
        songs: Song[];
      }>(`/contributors/${slug}`),
  });
  if (!data) return null;
  return (
    <div className="page">
      <div className="profile-hero">
        <div className="avatar large">{data.user.displayName.slice(0, 2).toUpperCase()}</div>
        <div>
          <span className="kicker">Contributor</span>
          <h1>{data.user.displayName}</h1>
          <p>{data.user.biography ?? 'Helping guitarists play more of the music they love.'}</p>
          <strong>{data.user.approvedContributionCount} approved contributions</strong>
        </div>
      </div>
      <div className="song-grid">
        {data.songs.map((s) => (
          <SongCard key={s.id} song={s} />
        ))}
      </div>
    </div>
  );
}
export function Requests() {
  const { data } = useQuery({
    queryKey: ['requests'],
    queryFn: () =>
      api<{ items: { id: string; title: string; artist_name: string; vote_count: number }[] }>(
        '/requests',
      ),
  });
  return (
    <div className="page">
      <div className="section-heading">
        <div>
          <span className="kicker">Community wishlist</span>
          <h1>Song requests</h1>
          <p>Vote for the songs you want the community to chart next.</p>
        </div>
        <Link className="button" to="/dashboard/requests">
          Request a song
        </Link>
      </div>
      <div className="request-list">
        {data?.items.map((r) => (
          <article key={r.id}>
            <div>
              <h3>{r.title}</h3>
              <p>{r.artist_name}</p>
            </div>
            <button onClick={() => void post(`/requests/${r.id}/vote`)}>▲ {r.vote_count}</button>
          </article>
        ))}
      </div>
    </div>
  );
}
export function About() {
  return (
    <div className="prose page">
      <span className="kicker">Our community</span>
      <h1>Play it forward.</h1>
      <p>
        ChordVerse is a shared songbook built by musicians. We publish chord sheets that are
        accurate, readable, and respectful of artists.
      </p>
      <h2>Community guidelines</h2>
      <p>
        Submit your own chord transcriptions, credit artists accurately, keep formatting clear, and
        treat fellow contributors with respect. Reviewers may request changes to improve accuracy or
        readability.
      </p>
      <h2>Copyright</h2>
      <p>
        ChordVerse provides user-contributed chord interpretations for education. Copyright owners
        can report content from any song page for prompt review.
      </p>
    </div>
  );
}
export function NotFound() {
  return (
    <div className="empty-page">
      <strong>404</strong>
      <h1>That note isn’t in this song.</h1>
      <p>The page may have moved or never existed.</p>
      <Link className="button" to="/">
        Back home
      </Link>
    </div>
  );
}
function Empty({ title, text }: { title: string; text: string }) {
  return (
    <div className="empty">
      <BookOpen />
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
